import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { serviceAPI, paymentAPI, messageAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './ServiceDetail.css';

const Stars = ({ rating }) => (
  <div className="stars">
    {[1,2,3,4,5].map(n => (
      <span key={n} className={`star ${n <= Math.round(rating) ? 'filled' : ''}`}>★</span>
    ))}
  </div>
);

export default function ServiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [checkingOut, setCheckingOut] = useState(false);
  const [notes, setNotes] = useState('');
  const [messaging, setMessaging] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    serviceAPI.getById(id)
      .then(r => setService(r.data))
      .catch(() => navigate('/404'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleCheckout = async () => {
    if (!user) { navigate('/login'); return; }
    setCheckingOut(true);
    try {
      const { data } = await paymentAPI.createCheckout({ serviceId: id, notes });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Checkout failed. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  const handleMessage = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!message.trim()) return;
    try {
      await messageAPI.send({ receiverId: service.sellerId, content: message });
      toast.success('Message sent!');
      setMessage('');
      setMessaging(false);
      navigate('/messages');
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;
  if (!service) return null;

  const isSeller = user?.id === service.sellerId;
  const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(service.title)}&size=600&background=FDE68A&color=92400E&bold=true`;
  const images = service.images?.length > 0 ? service.images : [null];

  return (
    <div className="service-detail page">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Home</Link> ›
          <Link to="/browse">Browse</Link> ›
          {service.category && <Link to={`/browse?category=${service.category.slug}`}>{service.category.name}</Link>}
          › <span>{service.title}</span>
        </div>

        <div className="detail-grid">
          {/* Left: Images + Description */}
          <div className="detail-left">
            {/* Gallery */}
            <div className="gallery">
              <div className="gallery-main">
                <img
                  src={images[activeImg] ? `http://localhost:5000${images[activeImg]}` : fallback}
                  alt={service.title}
                  onError={e => { e.target.src = fallback; }}
                />
              </div>
              {images.length > 1 && (
                <div className="gallery-thumbs">
                  {images.map((img, i) => (
                    <button key={i} className={`gallery-thumb ${i === activeImg ? 'active' : ''}`} onClick={() => setActiveImg(i)}>
                      <img
                        src={img ? `http://localhost:5000${img}` : fallback}
                        alt={`View ${i + 1}`}
                        onError={e => { e.target.src = fallback; }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Description */}
            <div className="detail-section">
              <h2>About This Service</h2>
              <p className="detail-description">{service.description}</p>
            </div>

            {/* Tags */}
            {service.tags?.length > 0 && (
              <div className="detail-section">
                <h3>Tags</h3>
                <div className="tags">
                  {service.tags.map(t => <span key={t} className="tag">{t}</span>)}
                </div>
              </div>
            )}

            {/* Reviews */}
            <div className="detail-section">
              <h2>Reviews ({service.totalReviews})</h2>
              {service.reviews?.length > 0 ? (
                <div className="reviews-list">
                  {service.reviews.map(review => (
                    <div key={review.id} className="review-card">
                      <div className="review-header">
                        <div className="avatar">
                          {review.author.avatar
                            ? <img src={`http://localhost:5000${review.author.avatar}`} alt={review.author.firstName} />
                            : `${review.author.firstName?.[0]}${review.author.lastName?.[0]}`
                          }
                        </div>
                        <div>
                          <div className="review-author">{review.author.firstName} {review.author.lastName}</div>
                          <Stars rating={review.rating} />
                        </div>
                        <span className="review-date">{new Date(review.createdAt).toLocaleDateString()}</span>
                      </div>
                      {review.comment && <p className="review-comment">{review.comment}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-light">No reviews yet. Be the first!</p>
              )}
            </div>
          </div>

          {/* Right: Booking Card + Seller */}
          <div className="detail-right">
            {/* Booking Card */}
            <div className="booking-card">
              <div className="booking-price">
                <span className="price-label">
                  {service.priceType === 'starting_at' ? 'Starting at' : service.priceType === 'hourly' ? 'Hourly rate' : 'Fixed price'}
                </span>
                <span className="price-big">${service.price?.toFixed(2)}</span>
                {service.priceType === 'hourly' && <span className="price-unit">/hr</span>}
              </div>

              <div className="booking-meta">
                <Stars rating={service.avgRating} />
                <span className="meta-text">{service.avgRating?.toFixed(1)} ({service.totalReviews} reviews)</span>
              </div>

              {service.deliveryTime && (
                <div className="booking-info-row">
                  <span>⏱️ Delivery</span>
                  <span>{service.deliveryTime}</span>
                </div>
              )}
              {(service.city || service.state) && (
                <div className="booking-info-row">
                  <span>📍 Location</span>
                  <span>{[service.city, service.state].filter(Boolean).join(', ')}</span>
                </div>
              )}
              <div className="booking-info-row">
                <span>📦 Orders</span>
                <span>{service.totalOrders} completed</span>
              </div>

              {!isSeller && (
                <>
                  <div className="form-group mt-2">
                    <label className="form-label">Order Notes (optional)</label>
                    <textarea
                      className="form-textarea"
                      placeholder="Any specific requirements or details..."
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      rows={3}
                    />
                  </div>

                  <button
                    className="btn btn-primary btn-block btn-lg"
                    onClick={handleCheckout}
                    disabled={checkingOut}
                  >
                    {checkingOut ? '⏳ Processing...' : '🛒 Book & Pay Securely'}
                  </button>

                  <button
                    className="btn btn-outline btn-block mt-1"
                    onClick={() => setMessaging(!messaging)}
                  >
                    💬 Message Seller
                  </button>

                  {messaging && (
                    <form onSubmit={handleMessage} className="message-form">
                      <textarea
                        className="form-textarea"
                        placeholder="Write your message..."
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        required
                        rows={3}
                      />
                      <button type="submit" className="btn btn-secondary btn-block btn-sm">Send Message</button>
                    </form>
                  )}

                  <p className="secure-note">🔒 Secure checkout powered by Stripe</p>
                </>
              )}

              {isSeller && (
                <Link to={`/dashboard/seller/edit/${service.id}`} className="btn btn-outline btn-block">
                  ✏️ Edit This Service
                </Link>
              )}
            </div>

            {/* Seller Card */}
            {service.seller && (
              <div className="seller-card">
                <h3>About the Seller</h3>
                <Link to={`/sellers/${service.seller.id}`} className="seller-info">
                  <div className="avatar avatar-lg">
                    {service.seller.avatar
                      ? <img src={`http://localhost:5000${service.seller.avatar}`} alt={service.seller.firstName} />
                      : `${service.seller.firstName?.[0]}${service.seller.lastName?.[0]}`
                    }
                  </div>
                  <div>
                    <div className="seller-name">{service.seller.businessName || `${service.seller.firstName} ${service.seller.lastName}`}</div>
                    <div className="seller-stats">
                      {service.seller._count?.services} services •
                      {service.seller._count?.reviewsReceived} reviews
                    </div>
                  </div>
                </Link>
                {service.seller.businessDescription && (
                  <p className="seller-bio">{service.seller.businessDescription}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
